import { MenuItem } from '@mui/material';
import { Permissions } from 'Permission';
import { PermissionGate } from 'PermissionContext';
import React from 'react';
import { GlobalConfiguration } from '../../../models/GlobalConfiguration';
import StyledFieldDescription from '../../styles/StyledFieldDescription';
import { StyledInput } from '../../styles/StyledInputs';
import StyledSection from '../../styles/StyledSection';
import { StyledFieldRow, StyledHeading, StyledLabel, StyledTypography } from '../../styles/styles';
import VeraGlobalChatPromptOverrideAdmin from '../VeraGlobalChatPromptOverrideAdmin';
import ConfigFieldLabel from './ConfigFieldLabel';
import { GLOBAL_CONFIG_FIELD_HELP, GLOBAL_CONFIG_FIELD_LABELS } from './globalConfigFieldConfig';

type VeraPromptProposalsSectionProps = {
  config: GlobalConfiguration;
  dirtyFields: Set<string>;
  dropdownSx: object;
  onChange: (field: string, value: unknown) => void;
};

type ProposalNotificationFieldKey =
  | 'veraPromptProposalNotificationChannel'
  | 'veraPromptProposalNotificationGoogleChatWebhookUrl'
  | 'veraPromptProposalNotificationEmail';

type ProposalFieldProps = {
  fieldKey: ProposalNotificationFieldKey;
  dirtyFields: Set<string>;
  description: React.ReactNode;
  children: React.ReactNode;
};

/** Label + description scaffold shared by the three proposal-notification rows. */
function ProposalField({ fieldKey, dirtyFields, description, children }: ProposalFieldProps) {
  return (
    <StyledFieldRow stacked sx={{ mb: 2 }} data-field={fieldKey}>
      <StyledLabel stacked>
        <ConfigFieldLabel
          fieldKey={fieldKey}
          label={GLOBAL_CONFIG_FIELD_LABELS[fieldKey]}
          dirtyFields={dirtyFields}
          tooltip={GLOBAL_CONFIG_FIELD_HELP[fieldKey]}
        />
      </StyledLabel>
      <StyledFieldDescription stacked>{description}</StyledFieldDescription>
      {children}
    </StyledFieldRow>
  );
}

function ChannelSelect({
  config,
  dropdownSx,
  onChange,
}: Omit<VeraPromptProposalsSectionProps, 'dirtyFields'>) {
  return (
    <StyledInput
      select
      sx={dropdownSx}
      value={config.veraPromptProposalNotificationChannel ?? ''}
      onChange={(e) =>
        onChange(
          'veraPromptProposalNotificationChannel',
          e.target.value === '' ? null : e.target.value,
        )
      }
    >
      <MenuItem value="">None</MenuItem>
      <MenuItem value="google_chat">Google Chat</MenuItem>
      <MenuItem value="email">Email</MenuItem>
    </StyledInput>
  );
}

function ProposalNotifications({
  config,
  dirtyFields,
  dropdownSx,
  onChange,
}: VeraPromptProposalsSectionProps) {
  return (
    <StyledSection tone="tone1">
      <StyledHeading tone="tone1" headerVariant="heading2">
        Notifications
      </StyledHeading>
      <StyledFieldDescription stacked sx={{ mb: 2 }}>
        How reviewers are alerted when someone submits a Vera prompt proposal in the sandbox below.
        Proposals are always saved regardless of these settings — this only controls notifications.
        Use the page&apos;s Save button to persist changes to these fields.
      </StyledFieldDescription>

      <ProposalField
        fieldKey="veraPromptProposalNotificationChannel"
        dirtyFields={dirtyFields}
        description={
          <>Where a submitted proposal is announced. &quot;None&quot; disables notifications.</>
        }
      >
        <ChannelSelect config={config} dropdownSx={dropdownSx} onChange={onChange} />
      </ProposalField>

      <ProposalField
        fieldKey="veraPromptProposalNotificationGoogleChatWebhookUrl"
        dirtyFields={dirtyFields}
        description="Used when the channel is Google Chat. If blank, proposals are still saved but no chat notification is sent."
      >
        <StyledInput
          fullWidth
          value={config.veraPromptProposalNotificationGoogleChatWebhookUrl ?? ''}
          onChange={(e) =>
            onChange('veraPromptProposalNotificationGoogleChatWebhookUrl', e.target.value || null)
          }
        />
        <StyledTypography
          component="p"
          fontSize="0.75rem"
          sx={{ display: 'block', mt: 0.5, color: 'text.secondary' }}
        >
          In Google Chat: open the target space &rarr; Apps &amp; integrations &rarr; Add webhooks
          &rarr; name and save &rarr; copy the generated URL.
        </StyledTypography>
      </ProposalField>

      <ProposalField
        fieldKey="veraPromptProposalNotificationEmail"
        dirtyFields={dirtyFields}
        description="Recipient address when the channel is Email. If blank, proposals are still saved but no email is sent."
      >
        <StyledInput
          fullWidth
          type="email"
          value={config.veraPromptProposalNotificationEmail ?? ''}
          onChange={(e) => onChange('veraPromptProposalNotificationEmail', e.target.value || null)}
        />
      </ProposalField>
    </StyledSection>
  );
}

export default function VeraPromptProposalsSection(props: VeraPromptProposalsSectionProps) {
  return (
    <>
      <ProposalNotifications {...props} />

      <PermissionGate permissionKey={Permissions.AIDeveloper}>
        <VeraGlobalChatPromptOverrideAdmin />
      </PermissionGate>
    </>
  );
}
